import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { trackEvent } from "src/Amplitude";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import {
  CloseButton,
  Header,
  HeaderAndSearch,
  SearchBar,
  SearchBarIcon,
  SearchBarWrapper,
  Wrapper,
} from "src/components/common/crypto-selection-modalV2/CryptoSelectionModalV2";
import { TargetOrderHoldingsSkeleton } from "src/components/common/Skeletons";
import MagnifyingGlass from "src/icons/MagnifyingGlass";
import api from "src/store/features/api";
import {
  SelectedCurrencyType,
  setCompletedFormSection,
  setSelectedCurrency,
  useTargetState,
} from "src/store/features/TargetOrders.slice";
import { useAppDispatch } from "src/store/store";
import { dynamicUnitFormatter } from "src/utils/helpers/PriceAndUnitsHelpers";
import { TargetOrderStyles } from "../../styles/TargetOrder.style";
import { NoHoldings } from "./TargetTools";

type SellDropDownProps = {
  userDefaultCurrency?: any;
  fee?: number;
};

export const TargetOrderFiatStableHoldingsModal = ({
  userDefaultCurrency,
  fee,
}: SellDropDownProps) => {
  //----------------------Styles---------------------
  const classes = TargetOrderStyles();
  const theme = useTheme();
  //----------------------State---------------------
  const [modal, setModal] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  //----------------------Redux---------------------
  const dispatch = useAppDispatch();
  const { selectedCurrency } = useTargetState();
  const {
    data: userHoldings,
    refetch,
    isLoading,
  } = api.useGetWalletPageHoldingsQuery({
    baseCurrency: userDefaultCurrency,
  });

  //----------------------CONSTS---------------------

  const treatedHoldings = userHoldings
    ?.filter(
      (el) => el.currencyConfig.primarySector === "Stablecoin" || el.currencyConfig.type === "Fiat",
    )
    .filter((el) => el.currencyConfig.name?.toLowerCase().includes(searchVal.toLocaleLowerCase()))
    .filter((el) => el.available !== 0)
    .filter((item) => item.available > 0);

  //----------------------Hooks---------------------

  useEffect(() => {
    refetch();
  }, []);

  //----------------------Functions---------------------

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const handleCoinSelect = (event: React.MouseEvent, selectedCurrency: SelectedCurrencyType) => {
    const body: SelectedCurrencyType = {
      code: selectedCurrency.code,
      name: selectedCurrency.name,
      type: selectedCurrency.type,
      icon: selectedCurrency.icon,
      available: selectedCurrency.available,
      selected: true,
    };

    dispatch(setCompletedFormSection("currencyInput"));
    dispatch(setSelectedCurrency(body));
    handleModalToggle();
    trackEvent("Select_Target_Order_Currency", {
      Selected_Currency: selectedCurrency.code,
    });
  };
  const handleModalToggle = () => {
    setModal((prev) => !prev);
  };

  return (
    <>
      <Button
        disableRipple
        id="token-select-currency-btn-sell"
        onClick={handleModalToggle}
        sx={{
          height: "33px",
          borderRadius: "10px",
          padding: "8px",
          width: "auto",
          alignItems: "center",
          justifyContent: "normal !important",
          backgroundColor: selectedCurrency.code ? "#E8F6FF" : theme.palette.primary.main,
          "&:hover": { backgroundColor: " #4e7086" },
        }}
        endIcon={!selectedCurrency.code && <GridExpandMoreIcon sx={{ color: "#fff" }} />}>
        <Stack direction="row" justifyContent="center" alignItems="center">
          {selectedCurrency.code && (
            <Avatar style={{ backgroundColor: "transparent" }}>
              <CountryOrCryptoFlag
                name={selectedCurrency.icon || selectedCurrency.code}
                size={20}
              />
            </Avatar>
          )}
          <Typography
            sx={{
              color: selectedCurrency.code ? theme.palette.primary.main : "#fff",
              fontWeight: 600,
              lineHeight: "17px",
              fontSize: "14px",
              textTransform: "capitalize",
            }}>
            {selectedCurrency.code ? selectedCurrency.code : "Select Currency"}
          </Typography>
        </Stack>
      </Button>
      <Wrapper
        open={modal}
        onClose={handleModalToggle}
        sx={{ "& .MuiDialog-paper": { height: "fit-content" } }}>
        <HeaderAndSearch>
          {/* header */}
          <Header>
            Select Currency to Sell
            {/* close button */}
            <CloseButton onClick={handleModalToggle}>
              <CloseIcon fontSize="inherit" />
            </CloseButton>
          </Header>

          {/* searchbar */}
          <SearchBarWrapper>
            <SearchBar
              autoFocus
              placeholder="Search"
              onChange={handleSearchChange}
              value={searchVal}
            />
            <SearchBarIcon>
              <MagnifyingGlass />
            </SearchBarIcon>
          </SearchBarWrapper>
        </HeaderAndSearch>
        <Grid container>
          <List className={classes.list} id="select-currency-list">
            {isLoading && !treatedHoldings && <TargetOrderHoldingsSkeleton />}
            {!treatedHoldings?.length && !isLoading && (
              <Box sx={{ paddingRight: "10px", paddingLeft: "10px" }}>
                <NoHoldings origin="target-buy" />
              </Box>
            )}
            {treatedHoldings?.map((el: any, idx: any) => (
              <Button
                disableRipple
                id={`select-sell-token-${el?.currencyConfig.code}`}
                fullWidth
                key={idx}
                onClick={(event) =>
                  handleCoinSelect(event, {
                    name: el.currencyConfig.name,
                    code: el.currencyConfig.code,
                    icon: el.currencyConfig.logoUrl,
                    type: el.currencyConfig.type,
                    available: el.available,
                    selected: true,
                  })
                }>
                <Grid container>
                  <ListItem data-code={el?.currencyConfig.code} data-name={el?.currencyConfig.name}>
                    <ListItemAvatar>
                      <Avatar style={{ backgroundColor: "transparent" }}>
                        <CountryOrCryptoFlag
                          name={el?.currencyConfig.logoUrl || el?.currencyConfig.code}
                          size={35}
                        />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={el?.currencyConfig.name.replace(/\s*\(.*?\)\s*/g, "")}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                      sx={{
                        textTransform: "none",
                        flexBasis: "auto",
                      }}
                    />

                    <ListItemText
                      sx={{
                        textTransform: "none",
                        textAlign: "end",
                      }}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                      primary={dynamicUnitFormatter(el.available, 0) + " " + el.currencyConfig.code}
                    />
                  </ListItem>
                </Grid>
              </Button>
            ))}
          </List>
        </Grid>
      </Wrapper>
    </>
  );
};
export default TargetOrderFiatStableHoldingsModal;
